import React from 'react'
import { ILearningResourceCategory } from 'marketing-site/@types/generated/contentful'
import { containerMaxWidth } from 'marketing-site/src/library/utils'
import { mq } from 'marketing-site/src/library/utils'
import { Tabs, TabList, Tab, TabPanels, TabPanel } from '@reach/tabs'
import { ResourcesByCategory } from 'marketing-site/src/components/bespoke/resources/resourcesbycategory'
import { Categories } from 'marketing-site/src/library/utils/constants/categories'
import { IProps as IResourceHubProps } from './index'
import { Text } from 'marketing-site/src/library/elements/Text'
import useTranslate from 'marketing-site/src/library/hooks/useTranslate'

const getCategoriesToShow = (
  categories: ILearningResourceCategory[],
  categoryToShow: Categories.Topic | Categories.Media,
) => {
  return categories?.filter((category) => category.fields.eyebrow === categoryToShow)
}

export const ResourceHub = ({ learnDirectory }: IResourceHubProps): React.ReactElement => {
  const topicCategories = getCategoriesToShow(learnDirectory.categories, Categories.Topic)
  const mediaCategories = getCategoriesToShow(learnDirectory.categories, Categories.Media)
  const { translate } = useTranslate()

  const TopicsTitle = `${Categories.Topic}s`
  return (
    <>
      <div className="resource-hub">
        <div className="resource-hub__inner">
          <div className="resource-hub__inner-wrapper">
            <div className="resource-hub__collection-title">
              <Text brand="default" size="heading-2">
                {translate('Collections')}
              </Text>
            </div>

            <Tabs>
              <TabList className="resource-hub__tablist">
                <Tab>
                  <Text brand="default" size="body">
                    {TopicsTitle}
                  </Text>
                </Tab>
                <Tab>
                  <Text brand="default" size="body">
                    {Categories.Media}
                  </Text>
                </Tab>
              </TabList>
              <TabPanels>
                <TabPanel>
                  {topicCategories?.length > 0 && (
                    <ResourcesByCategory categories={topicCategories} />
                  )}
                </TabPanel>
                <TabPanel>
                  {mediaCategories?.length > 0 && (
                    <ResourcesByCategory categories={mediaCategories} />
                  )}
                </TabPanel>
              </TabPanels>
            </Tabs>
          </div>
        </div>
      </div>
      <style jsx>{`
        .resource-hub {
          background: $stone;
          margin-top: 2rem;
          position: relative;
          display: grid;
          padding: 3rem 0;
        }

        .resource-hub__inner {
          position: relative;
          max-width: ${containerMaxWidth};
          margin: 0 auto;
          display: grid;
          grid-template-columns: [left-gutter]1fr [content]12fr [right-gutter]1fr;
        }

        .resource-hub__inner:before {
          content: '';
        }

        .resource-hub__collection-title {
          position: relative;
          font-size: 3.2rem;
          font-weight: 600;
          line-height: 1.167;
          padding-bottom: 1rem;
        }

        .resource-hub :global([data-reach-tab-list]) {
          border: 2px solid;
          border-radius: 4rem;
          overflow: hidden;
          display: inline-block;
          position: relative;
        }

        .resource-hub :global([data-reach-tab]) {
          cursor: pointer;
        }

        .resource-hub :global([data-reach-tab][data-focus-visible-added]:after) {
          content: '';
          pointer-events: none;
          display: block;
          position: absolute;
          top: -6px;
          bottom: -6px;
          left: -6px;
          right: -6px;
          border-radius: 7px;
          border: 3px solid;
        }

        /* Disable outline when clicking inside the tab panel */
        .resource-hub :global([data-reach-tab-panel]:not([data-focus-visible-added])) {
          outline: 0;
        }

        .resource-hub :global([data-reach-tab]) {
          padding: 0.7rem 3rem;
          font-weight: bold;
          outline: 0;
          min-width: 147px;
        }

        .resource-hub :global([data-reach-tab]):first-child {
          border-radius: 4rem 0 0 4rem;
        }
        .resource-hub :global([data-reach-tab]):last-child {
          border-radius: 0 4rem 4rem 0;
        }

        .resource-hub :global([data-reach-tab]:not(:first-child)) {
          font-weight: bold;
          border-inline-start: 2px solid;
        }

        .resource-hub :global([data-reach-tab]) {
          background: $white;
        }

        .resource-hub :global([data-reach-tab][data-selected]) {
          background: $gray;
        }

        :global(.resource-hub h2.learn-link__header > span:nth-child(1)),
        :global(.resource-hub h2.customer__story-header > span:nth-child(1)) {
          font-size: 2rem;
          max-width: 28rem;
        }

        @media (${mq.tablet}) {
          :global(.resource-hub h2.learn-link__header > span:nth-child(1)),
          :global(.resource-hub h2.customer__story-header > span:nth-child(1)) {
            font-size: 2.75rem;
          }
        }

        @media (${mq.laptop}) {
          :global(.resource-hub h2.learn-link__header > span:nth-child(1)),
          :global(.resource-hub h2.customer__story-header > span:nth-child(1)) {
            max-width: unset;
          }
        }

        @media (${mq.desktop}) {
          .resource-hub {
            margin-top: 0;
            padding: 4.5rem 0;
          }

          .resource-hub {
            :global(.resource-hub__tablist) {
              position: absolute;
              top: 0%;
              left: 64%;
            }
          }
        }

        @media (min-width: 1280px) {
          .resource-hub {
            :global(.resource-hub__tablist) {
              left: 70%;
            }
          }
        }

        @media (${mq.desktopLg}) {
          .resource-hub__inner {
            grid-template-columns: [left-gutter]0.5fr [content]14fr [right-gutter]0.5fr;
          }

          .resource-hub {
            :global(.resource-hub__tablist) {
              left: 74%;
            }
          }
        }
      `}</style>
    </>
  )
}
